import { useState } from 'react'
import {
  Button,
  Fa,
  Modal,
  TextHeader,
  Text,
  FormItem,
  RadioButton,
  RadioButtonGroup,
  TextInput,
  Tooltip,
  showErrorMessage,
} from '@eltoro-ui/components'
import { Campaignservicev1OrderLine } from 'next-gen-sdk'
import { useNavigate } from 'react-router-dom'
import { ApiFactory } from 'apiFactory'
import { CampaignSelect } from '../CampaignSelect/CampaignSelect'
import { styleTailwind } from 'Helpers'
import classNames from 'classnames'
import { ETIcon } from 'Assets'
import { useAppContext } from 'Contexts'

const CampaignLabel = styleTailwind(
  'div',
  'Campaign__label flex flex-1 flex-col leading-none justify-start max-w-14 w-[9vw]',
)

const IconWrap = styleTailwind(
  'figure',
  'Campaign__icon-wrap flex items-center justify-center h-10 w-10',
)

const CampaignLabelWrap = styleTailwind(
  'div',
  'Campaign__label-wrap flex leading-none gap-3 items-center',
)

const CampaignInputWrap = styleTailwind(
  'div',
  'Campaign__input-wrap flex flex-1 leading-none items-start w-2/3',
)

const CampaignDetailRow = styleTailwind(
  'div',
  'Campaign__detail-row flex gap-[2.5vw] laptop:flex-1 desktop:pr-[2vw] desktop:pt-[2vw] justify-start w-full laptop:px-4',
)

type CopyOrderlineModalProps = {
  orderline: Campaignservicev1OrderLine
  isOpen: boolean
  onClose: () => void
}

export const CopyOrderlineModal = ({
  orderline,
  isOpen,
  onClose,
}: CopyOrderlineModalProps) => {
  const { currentOrg, campaignServiceApi } = useAppContext()
  const navigate = useNavigate()

  const [existingCampaignId, setExistingCampaignId] = useState('')
  const [showSelect, setShowSelect] = useState(false)
  const [showCampaignCreator, setShowCampaignCreator] = useState(false)
  const [political, setPolitical] = useState(orderline?.political || false)
  const [campName, setCampName] = useState('')

  const isDisabled = campName?.length <= 1 || (!currentOrg && !campName)
  const lengthLimit = campName?.length > 255
  const query = currentOrg?.id ? `?org_id=${currentOrg?.id}` : ''

  const handleClose = () => {
    setShowCampaignCreator(false)
    setShowSelect(false)
    setExistingCampaignId('')
    setCampName('')
    onClose()
  }

  const handleCopyOrderline = async (campaignId: string) => {
    if (orderline?.id && orderline.orgId) {
      campaignServiceApi
        ?.advertisingPlatformServiceCloneOrderLine(orderline?.id, {
          orgId: orderline.orgId,
          campaignId,
        })
        .then(() => {
          navigate(`/campaigns/edit/${campaignId}/settings${query}`)
        })
        .catch((error) => {
          showErrorMessage(
            'Failed to copy order line',
            error?.body?.message ||
              'The copy failed. This may be because no audiences or creatives remain after excluding archived, expired, purged, or not found items.',
          )
        })
    }
  }

  const handleCampaignCreationFromClone = async (
    cName: string,
    political?: boolean,
  ) => {
    const now = new Date()
    const sDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const eDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 14,
    )
    const base = {
      name: cName,
      startTime: sDate,
      endTime: eDate,
    }
    const { campaignServiceAPI } = ApiFactory(
      localStorage.getItem('eltoro_token'),
    )
    if (cName && eDate && sDate && orderline?.orgId) {
      campaignServiceAPI
        ?.advertisingPlatformServiceCreateCampaign(
          orderline.orgId,
          political ? { ...base, political } : { ...base },
        )
        .then((res) => {
          if (res.id && orderline?.id) {
            campaignServiceAPI
              ?.advertisingPlatformServiceCloneOrderLine(orderline?.id, {
                orgId: orderline.orgId,
                campaignId: res.id,
              })
              .then(() => {
                navigate(`/campaigns/edit/${res.id}/settings${query}`)
              })
              .catch((error) => {
                showErrorMessage(
                  'Failed to copy order line to new campaign',
                  <span>
                    The copy failed. This may be because no audiences or
                    creatives remain after excluding archived, expired, purged,
                    or not found items. If you need further assistance, please
                    contact &nbsp;
                    <a href="mailto:support@eltoro.com">support@eltoro.com</a>
                  </span>,
                )
              })
          }
        })
        .catch((error) => {
          showErrorMessage(
            'Failed to create campaign',
            error?.body?.message || 'Unable to create the new campaign.',
          )
        })
    }
  }

  if (!isOpen) return null

  return (
    <Modal
      offClick={handleClose}
      className={classNames({
        'min-h-fit min-w-[64rem]': showCampaignCreator,
        'min-w-[24rem]': showSelect,
      })}
    >
      <div className="pt-2 pb-2">
        <div
          className={`flex flex-row-reverse justify-between ${
            !showCampaignCreator && 'pb-4'
          }`}
        >
          <Button
            className="CopyOrderlineModal_Close_Button"
            kind="text"
            onClick={handleClose}
            iconOnly={<Fa icon="times" size={1} />}
          />
          <TextHeader type={3} className="CopyOrderlineModal_header">
            Copy Order Line
          </TextHeader>
        </div>

        <div className="bg-tint-gray-50 mb-6 rounded-lg px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <Fa icon="circle-info" className="text-tint-gray-500" size={1} />
            </div>
            <div className="flex-1">
              <p className="text-tint-gray-700 text-sm leading-relaxed">
                When copying this order line, all{' '}
                <span className="text-tint-gray-800 font-semibold">
                  audiences
                </span>{' '}
                and{' '}
                <span className="text-tint-gray-800 font-semibold">
                  creatives
                </span>{' '}
                will be included, except those that are archived, expired,
                purged, or not found.
              </p>
              <p className="text-tint-gray-800 mt-3 text-sm font-bold leading-relaxed">
                Note: The copy will FAIL if no audiences or creatives remain
                after exclusions.
              </p>
            </div>
          </div>
        </div>

        {!showSelect && !showCampaignCreator && (
          <div className="flex justify-center">
            <div className="p-4">
              <Button
                onClick={() => {
                  if (
                    orderline &&
                    orderline.campaign &&
                    orderline.campaign.id
                  ) {
                    handleCopyOrderline(orderline?.campaign?.id)
                  }
                }}
              >
                Same Campaign?
              </Button>
            </div>
            <div className="p-4">
              <Button
                onClick={() => {
                  setShowCampaignCreator(true)
                }}
              >
                New Campaign?
              </Button>
            </div>
            <div className="p-4">
              <Button
                onClick={() => {
                  setShowSelect(true)
                }}
              >
                Existing Campaign?
              </Button>
            </div>
          </div>
        )}
        {showSelect && (
          <div className="m-auto max-w-fit pt-4">
            <Text>Select an Existing Campaign</Text>
            <CampaignSelect
              isPrepay={orderline?.prepay}
              currentCampaignId={orderline?.campaign?.id}
              onCampaignSelect={(campId) => {
                setExistingCampaignId(campId)
              }}
            />
            <div className="flex flex-row justify-between pt-2">
              <Button
                onClick={() => {
                  setShowSelect(false)
                  setExistingCampaignId('')
                }}
              >
                Cancel?
              </Button>
              <Button
                onClick={() => {
                  if (!existingCampaignId) return
                  handleCopyOrderline(existingCampaignId)
                }}
                disabled={!existingCampaignId}
              >
                Copy to selected?
              </Button>
            </div>
          </div>
        )}
        {showCampaignCreator && (
          <div className="m-auto pr-2 pl-2 pt-2">
            <Text>
              Create a new Campaign, Your order line will be able to make
              modifications to the order line after creation
            </Text>
            <CampaignDetailRow className="items-start">
              <CampaignLabelWrap>
                <IconWrap>
                  <Fa
                    icon="fa-ad"
                    type="duotone"
                    size={5}
                    className="text-primary-400"
                  />
                </IconWrap>
                <CampaignLabel>
                  <TextHeader className="text-m normal-case" type={5}>
                    Campaign Name
                  </TextHeader>
                </CampaignLabel>
              </CampaignLabelWrap>
              <CampaignInputWrap className="CampaignSplash_Name-input flex-col gap-1">
                <TextInput
                  className="CampaignSplash_name-input-field text-tint-gray-800 text-l bg-tint-gray-50 py-2"
                  value={campName || ''}
                  onChange={(e) => setCampName(e.target.value)}
                  placeholder="Title of your advertising campaign"
                  size="m"
                  maxLength={256}
                  valid={!isDisabled && !lengthLimit && campName?.length > 1}
                  hideValidIcon
                />
                {lengthLimit && (
                  <div className="LengthLimit__error-message text-m text-warning-500 animate-slidedown pb-3 font-semibold">
                    Campaign name cannot exceed 255 characters
                  </div>
                )}
              </CampaignInputWrap>
            </CampaignDetailRow>
            <CampaignDetailRow className="items-center">
              <CampaignLabelWrap>
                <IconWrap>
                  <ETIcon icon="advocacy" />
                </IconWrap>
                <CampaignLabel>
                  <TextHeader className="text-m normal-case" type={5}>
                    Political
                  </TextHeader>
                  <small className="leading-none">
                    Campaigns that support political candidates, initiatives, or
                    advocacy causes
                  </small>
                </CampaignLabel>
              </CampaignLabelWrap>
              <CampaignInputWrap className="CampaignSplash_Political-checkbox gap-4">
                <FormItem htmlFor="political">
                  <RadioButtonGroup
                    className="PoliticalCampaign-radio-group"
                    layout="horizontal"
                    name="Political campaign"
                    valueSelected={political === true ? 'yes' : 'no'}
                    onChange={() => setPolitical(!political)}
                  >
                    <RadioButton
                      id="yes"
                      value="yes"
                      checked={political}
                      label="Yes"
                    />
                    <RadioButton
                      id="no"
                      value="no"
                      checked={!political}
                      label="No"
                    />
                  </RadioButtonGroup>
                </FormItem>
              </CampaignInputWrap>
            </CampaignDetailRow>
            <div className="flex justify-between pt-8">
              <Button
                onClick={() => {
                  setShowCampaignCreator(false)
                }}
              >
                Cancel?
              </Button>
              <Button
                onClick={() => {
                  handleCampaignCreationFromClone(campName, political)
                }}
              >
                Create?
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
